import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// e.g. 1 2 3 4 5 … 14  |  1 … 6 7 8 … 14
const buildPages = (page, totalPages) => {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const pages = new Set([1, totalPages, page - 1, page, page + 1]);
  if (page <= 4) [2, 3, 4, 5].forEach((p) => pages.add(p));
  if (page >= totalPages - 3) [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1].forEach((p) => pages.add(p));
  const sorted = [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  return sorted.flatMap((p, i) => (i > 0 && p - sorted[i - 1] > 1 ? [`gap-${p}`, p] : [p]));
};

export const ExerciseRangeInfo = ({ page, pageSize, total }) => {
  if (total === 0) {
    return <p className="text-zinc-500 text-sm" data-testid="exercise-range-info">Nenhum exercício encontrado</p>;
  }
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <p className="text-zinc-500 text-sm" data-testid="exercise-range-info">
      Exibindo {from}–{to} de {total} exercício{total === 1 ? '' : 's'}
    </p>
  );
};

export const PageSizeSelect = ({ value, onChange }) => (
  <label className="flex items-center gap-2 text-sm text-zinc-400">
    <span className="hidden sm:inline">Por página</span>
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="h-9 px-3 bg-zinc-950/50 border border-zinc-800 rounded-lg text-white cursor-pointer focus:border-neon-lime focus:outline-none"
      data-testid="exercise-page-size-select"
    >
      {PAGE_SIZE_OPTIONS.map((n) => (
        <option key={n} value={n}>{n} por página</option>
      ))}
    </select>
  </label>
);

const navBtn = 'h-9 px-3 inline-flex items-center gap-1 rounded-lg border text-sm font-bold transition-colors duration-150 disabled:opacity-30 disabled:cursor-not-allowed';

export const ExercisePagination = ({ page, totalPages, onPageChange, disabled }) => {
  if (totalPages <= 1) return null;
  return (
    <nav className="flex flex-wrap items-center justify-center gap-1.5 pt-6" data-testid="exercise-pagination">
      <button
        type="button"
        onClick={() => onPageChange(page - 1)}
        disabled={disabled || page <= 1}
        className={`${navBtn} border-zinc-800 text-zinc-300 hover:border-neon-lime/50 hover:text-white`}
        data-testid="exercise-page-prev"
      >
        <ChevronLeft className="w-4 h-4" /> Anterior
      </button>
      {buildPages(page, totalPages).map((p) =>
        typeof p === 'string' ? (
          <span key={p} className="px-1 text-zinc-600">…</span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onPageChange(p)}
            disabled={disabled}
            aria-current={p === page ? 'page' : undefined}
            className={`${navBtn} min-w-[2.25rem] justify-center ${
              p === page
                ? 'bg-neon-lime text-black border-neon-lime'
                : 'border-zinc-800 text-zinc-300 hover:border-neon-lime/50 hover:text-white'
            }`}
            data-testid={`exercise-page-${p}`}
          >
            {p}
          </button>
        )
      )}
      <button
        type="button"
        onClick={() => onPageChange(page + 1)}
        disabled={disabled || page >= totalPages}
        className={`${navBtn} border-zinc-800 text-zinc-300 hover:border-neon-lime/50 hover:text-white`}
        data-testid="exercise-page-next"
      >
        Próxima <ChevronRight className="w-4 h-4" />
      </button>
    </nav>
  );
};
